import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ChevronRight, Plus } from 'lucide-react';
import { mortgageApi, reApi } from './workspacesApi';
import { useOwnerMode } from './OwnerModeContext';
import OpportunityEmblem from './board/OpportunityEmblem';
import { addressLine } from './shared/address';
import { OPP_LOAN_TYPE_LABELS, labelFor } from './shared/labels';
import { formatDate } from './kit/time';

const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : null;
const cap = (s) => s ? s[0].toUpperCase() + s.slice(1).replace(/_/g, ' ') : s;
const when = (iso) => iso ? formatDate(iso) : '—';

// One row shape for both kinds so the list can be merged and sorted.
const fromMortgage = (o) => ({
  product: 'mortgage',
  id: o.opportunity_id,
  headline: addressLine(o.property_address) || cap(o.loan_purpose) || 'Mortgage',
  detail: [labelFor(OPP_LOAN_TYPE_LABELS, o.loan_type), o.credit_score ? `FICO ${o.credit_score}` : null].filter(Boolean).join(' · '),
  amount: fmt$(o.loan_amount),
  stage: cap(o.stage),
  closed: !!o.is_closed,
  updated_at: o.updated_at,
});
const fromRE = (o) => ({
  product: 'real_estate',
  id: o.opportunity_id,
  headline: addressLine(o.property_address) || (o.target_areas || []).join(', ') || cap(o.side),
  detail: cap(o.side || 'buyer'),
  amount: fmt$(o.side === 'seller' ? o.listing_price : (o.target_price_max || o.approved_amount)),
  stage: cap(o.stage),
  closed: !!o.is_closed,
  updated_at: o.updated_at,
});

// Per-product cap on one request (the API's `total` is just this page's size).
const OPP_LIMIT = 100;
const PRODUCT_NAMES = { mortgage: 'mortgage', real_estate: 'real estate' };
const nameList = (products) => products.map(k => PRODUCT_NAMES[k]).join(' and ');

// Fetch the lists the tenant is licensed for and merge them newest-first.
const FETCHERS = {
  mortgage: { load: (contactId) => mortgageApi.listOpps({ contact_id: contactId, include_closed: true, limit: OPP_LIMIT }), toRow: fromMortgage },
  real_estate: { load: (contactId) => reApi.listOpps({ contact_id: contactId, include_closed: true, limit: OPP_LIMIT }), toRow: fromRE },
};

// Contact profile → Opportunities tab: every mortgage and real-estate
// opportunity for this contact in one list, each with its emblem, sorted by
// last update. Rows open the matching Pipeline board.
export default function ContactOpportunities({ contactId }) {
  const { hasProduct } = useOwnerMode();
  // { rows, failed: products whose fetch failed, capped: products at the cap }
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    const products = Object.keys(FETCHERS).filter(hasProduct);
    Promise.all(products.map(k => FETCHERS[k].load(contactId).then(
      d => ({ product: k, items: d.items || [] }),
      () => ({ product: k, items: [], failed: true }),
    ))).then(results => {
      if (!alive) return;
      const rows = results
        .flatMap(r => r.items.map(FETCHERS[r.product].toRow))
        .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
      setResult({
        rows,
        failed: results.filter(r => r.failed).map(r => r.product),
        capped: results.filter(r => r.items.length >= OPP_LIMIT).map(r => r.product),
      });
    });
    return () => { alive = false; };
  }, [contactId, hasProduct, attempt]);

  // Opens the Pipeline's create dialog with this contact already picked. Mortgage board only.
  const newOpp = hasProduct('mortgage') && (
    <Link to={`/portal/pipeline?board=mortgage&new_for=${encodeURIComponent(contactId)}`}
      className="btn-gold inline-flex items-center gap-1 text-sm" data-testid="contact-opps-new">
      <Plus size={14} /> New opportunity
    </Link>
  );

  const retry = () => { setResult(null); setAttempt(n => n + 1); };

  if (result === null) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="contact-opps-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading opportunities...
      </div>
    );
  }
  const { rows, failed, capped } = result;
  const retryButton = (
    <button type="button" onClick={retry} className="underline font-semibold" data-testid="contact-opps-retry">Retry</button>
  );
  if (rows.length === 0 && failed.length > 0) {
    return (
      <div role="alert" className="bg-red-50 border border-dashed border-red-200 rounded-2xl p-12 text-center text-red-700" data-testid="contact-opps-error">
        <p className="text-sm">Couldn't load {nameList(failed)} opportunities.</p>
        <p className="text-xs mt-1">{retryButton}</p>
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="contact-opps-empty">
        <p className="text-sm">No opportunities yet.</p>
        {newOpp
          ? <div className="mt-3">{newOpp}</div>
          : <p className="text-xs mt-1">Create one from the <Link to="/portal/pipeline" className="underline">Pipeline</Link>.</p>}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {newOpp && <div className="flex justify-end">{newOpp}</div>}
      {failed.length > 0 && (
        <div role="alert" className="text-xs rounded-lg bg-red-50 text-red-700 px-3 py-2" data-testid="contact-opps-error">
          Couldn't load {nameList(failed)} opportunities. {retryButton}
        </div>
      )}
      {capped.length > 0 && (
        <div className="text-xs rounded-lg bg-slate-50 text-slate-500 px-3 py-2" data-testid="contact-opps-capped">
          Showing the {OPP_LIMIT} most recent {nameList(capped)} opportunities. Older ones are not listed here.
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm divide-y divide-slate-100" data-testid="contact-opps-list">
        {rows.map(r => (
          <Link
            key={`${r.product}-${r.id}`}
            to={`/portal/pipeline?board=${r.product}&open=${r.id}`}
            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition"
            data-testid={`contact-opp-${r.id}`}
          >
            <OpportunityEmblem product={r.product} size={14} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate text-brand-navy">{r.headline}</div>
              <div className="text-xs text-slate-500 truncate">{r.detail || '—'} · {r.stage}{r.closed ? ' · Closed' : ''}</div>
            </div>
            <div className="text-right flex-shrink-0">
              {r.amount && <div className="text-sm font-bold text-brand-accent">{r.amount}</div>}
              <div className="text-[11px] text-slate-400">Updated {when(r.updated_at)}</div>
            </div>
            <ChevronRight size={14} className="text-slate-300 flex-shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}
